import React, { useState, useEffect, useRef } from 'react'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import toFixed from '../lib/toFixed'
import shotsToZero from '../lib/shots-to-zero'
import { drawPoint, drawX } from '../lib/canvas'
import { groupCenter } from '../lib/stats'


const ShotsToZero = () => {
  const [ sd, setSd ] = useAutoSaveState('sd', '.25')
  const [ avgFiveShot, setAvgFiveShot ] = useAutoSaveState('avgFiveShot', '.5')
  const [ scopeAdjustment, setScopeAdjustment ] = useAutoSaveState('scopeAdjustment', '.262')
  const [ , setPreview ] = useAutoSaveState('preview', null)
  const [ result, setResult ] = useState('')
  const [ examples, setExamples ] = useState([])

  const exampleRefs = useRef([])

  const calculate = () => {
    const result = shotsToZero(parseFloat(sd), parseFloat(scopeAdjustment))
    const canvases = []
    result.forEach((r, index) => {
      r.pointSamples.forEach((sample, i) => {
        canvases.push({
          id: `${index}-${i}`,
          sample,
          shots: r.shots,
          percent: r.percent,
        })
      })
    })
    setResult(result)
    setExamples(canvases)
    setPreview(`${toFixed(result, 0)}`)
    window.gtag('event', 'calculate_shots_to_zero')
  }

  useEffect(() => {
    examples.forEach((example, i) => {
      const canvas = document.createElement('canvas')
      canvas.width = 150
      canvas.height = 150
      canvas.style.width = '90%'
      canvas.style.border = '1px solid black'
      canvas.style.margin = '5px'
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, 100, 100)
      ctx.strokeStyle = '#aaa'
      const pxScale = 50
      const center = example.sample.center
      example.sample.points.forEach(point => {
        drawPoint(
          ctx,
          (point.x * pxScale) + canvas.width / 2,
          (point.y * pxScale) + canvas.height / 2,
          example.sample.zeroed ? 'green' : 'red',
          2
        )
      })
      drawX(ctx, { x: center.x * pxScale + canvas.width / 2, y: center.y * pxScale + canvas.height / 2 }, 10, 'black')
      //drawX(ctx, { x: canvas.width / 2, y: canvas.height / 2 }, 10, 'black')
      //ctx.beginPath()
      //ctx.arc(canvas.width / 2, canvas.height / 2, pxScale * scopeAdjustment / 2, 0, 2 * Math.PI)
      //ctx.stroke()
      // draw box in center
      ctx.beginPath()
      ctx.rect(
        canvas.width / 2 - pxScale * scopeAdjustment / 2,
        canvas.height / 2 - pxScale * scopeAdjustment / 2,
        pxScale * scopeAdjustment,
        pxScale * scopeAdjustment
      )
      ctx.rect(
        canvas.width / 2 - pxScale * scopeAdjustment,
        canvas.height / 2 - pxScale * scopeAdjustment,
        pxScale * scopeAdjustment * 2,
        pxScale * scopeAdjustment * 2
      )
      ctx.stroke()
      ctx.fillStyle = 'black'
      ctx.fillText(`${example.shots} ${example.shots === 1 ? 'shot': 'shots'}`, 5, 15)
      ctx.fillText(`${center.y > 0 ? 'up' : 'down'}=${Math.abs(center.y).toFixed(2)}`, 5, 25)
      ctx.fillText(`${center.x > 0 ? 'left' : 'right'}=${Math.abs(center.x).toFixed(2)}`, 5, 35)
      exampleRefs.current[i].replaceChildren(canvas)
    })
  }, [examples])

  const onAvgFiveShotChange = (e) => {
    setAvgFiveShot(e.target.value)
    setSd(((parseFloat(e.target.value) / 2) || 0).toFixed(3))
  }

  const onSdChange = (e) => {
    setSd(e.target.value)
    setAvgFiveShot(((parseFloat(e.target.value) * 2) || 0).toFixed(3))
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center">
        <Grid container justifyContent="center" xs={12} sx={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Shots Needed to Zero
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={5}>
                <TextField fullWidth label="Average Five Shot Group" autoFocus value={avgFiveShot} onChange={onAvgFiveShotChange} InputProps={{ endAdornment: <InputAdornment position="end">moa</InputAdornment> }} />
              </Grid>
              <Grid item xs={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                OR
              </Grid>
              <Grid item xs={5}>
                <TextField fullWidth label="Dispersion Standard Deviation" autoFocus value={sd} onChange={onSdChange} InputProps={{ endAdornment: <InputAdornment position="end">moa</InputAdornment> }} />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Scope Adjustment</InputLabel>
                  <Select
                    value={scopeAdjustment}
                    onChange={e => setScopeAdjustment(e.target.value)}
                    label="Scope Adjustment"
                  >
                    <MenuItem value=".131">1/8 MOA</MenuItem>
                    <MenuItem value=".262">1/4 MOA</MenuItem>
                    <MenuItem value=".524">1/2 MOA</MenuItem>
                    <MenuItem value="1.047">1 MOA</MenuItem>
                    <MenuItem value=".172">.05 Mil</MenuItem>
                    <MenuItem value=".3438">.1 Mil</MenuItem>
                    <MenuItem value=".688">.2 Mil</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
          </Paper>
        </Grid>
        {result && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Grid item xs={12}>
                <Typography variant="h5" gutterBottom style={{ width: '100%', textAlign: 'center' }}>
                  <EqualizerIcon /> Results
                </Typography>
                <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />
              </Grid>
              <Grid item xs={12}>
                <TableContainer>
                  <Table sx={{ maxWidth: '100%' }}>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th">Number of Shots</TableCell>
                        {result.map((r, i) => <TableCell key={i}>{r.shots}</TableCell>)}
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Chance of Zero On<br />Closest Click</TableCell>
                        {result.map((r, i) => <TableCell key={i}>{r.percent}</TableCell>)}
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Chance of Zero<br />Within 1 Click</TableCell>
                        {result.map((r, i) => <TableCell key={i}>{r.almostPercent}</TableCell>)}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h4" gutterBottom style={{ width: '100%', textAlign: 'center', margin: 10 }}>
                  Examples
                </Typography>
              </Grid>
              {examples.map((example, i) => (
                <Grid item xs={2} key={i} ref={el => exampleRefs.current[i] = el} />
              ))}
            </Grid>
          </Paper>
        </Grid>}
      </Grid>
    </Container>
  )
}

export default function ShotsToZeroTemplate () {
  return <AutoSaveProvider>
    <ShotsToZero  />
  </AutoSaveProvider>
}
